import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { LoaderScreen } from "../components/LoaderScreen";
import request from "../../Helpers/API";
import Swal from "sweetalert2";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
    ESTADO_COLORES,
    PRIORIDAD_COLORES,
    ACTIVIDAD_ESTADO_COLORES,
    Badge,
    useGestionTI,
} from "./gestionti-constants";

// Saludo compartido por las 3 vistas: mismo patrón visual (nombre + subtítulo)
// sin importar el rol, para que el módulo se sienta como una sola aplicación.
const primerNombre = (nombreCompleto) => (nombreCompleto ?? "").trim().split(" ")[0] || "—";

// ─── Vista Usuario ────────────────────────────────────────────────────────────

const VistaUsuario = ({ onNuevaSolicitud }) => {
    const { empleado } = useGestionTI();
    const [solicitudes, setSolicitudes]   = useState([]);
    const [loader, setLoader]             = useState({ mostrar: true, alerta: false });

    useEffect(() => {
        request
            .get("SolicitudesTI/Listado")
            .then((result) => {
                setSolicitudes(result ?? []);
                setLoader({ mostrar: false, alerta: false });
            })
            .catch((error) => {
                setLoader({ mostrar: false, alerta: false });
                Swal.fire("GESTIÓN TI", JSON.stringify(error), "error");
            });
    }, []);

    return (
        <div className="gestionti-vista">
            <div className="gestionti-vista-header">
                <div>
                    <span className="gestionti-vista-titulo">Hola, {primerNombre(empleado)}</span>
                    <span className="gestionti-vista-subtitulo">Solicitudes que has creado</span>
                </div>
                <button className="gestionti-btn-primary" onClick={onNuevaSolicitud}>
                    <AddIcon /> Nueva solicitud
                </button>
            </div>

            <LoaderScreen loader={loader} />

            <div className="gestionti-table-wrap scroll-4">
                {!loader.mostrar && solicitudes.length === 0 ? (
                    <div className="gestionti-empty">No tienes solicitudes registradas.</div>
                ) : (
                    <table className="gestionti-table" cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                <th>Folio</th>
                                <th>Tipo</th>
                                <th>Título</th>
                                <th>Estado</th>
                                <th>Prioridad</th>
                            </tr>
                        </thead>
                        <tbody>
                            {solicitudes.map((s) => (
                                <tr key={s.idsolicitud} className="gestionti-table-row">
                                    <td className="gestionti-td-folio">{s.folio}</td>
                                    <td className="gestionti-td-tipo">{s.tipo}</td>
                                    <td className="gestionti-td-titulo">{s.titulo}</td>
                                    <td><Badge texto={s.estado}    mapa={ESTADO_COLORES}    /></td>
                                    <td><Badge texto={s.prioridad} mapa={PRIORIDAD_COLORES} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

// ─── Vista Developer ──────────────────────────────────────────────────────────

const VistaDeveloper = () => {
    const { empleado } = useGestionTI();
    const [actividades, setActividades] = useState([]);
    const [loader, setLoader]           = useState({ mostrar: true, alerta: false });

    useEffect(() => {
        request
            .get("ActividadesTI/Mias")
            .then((result) => {
                setActividades(result ?? []);
                setLoader({ mostrar: false, alerta: false });
            })
            .catch((error) => {
                setLoader({ mostrar: false, alerta: false });
                Swal.fire("GESTIÓN TI", JSON.stringify(error), "error");
            });
    }, []);

    return (
        <div className="gestionti-vista">
            <div className="gestionti-vista-header">
                <div>
                    <span className="gestionti-vista-titulo">Hola, {primerNombre(empleado)}</span>
                    <span className="gestionti-vista-subtitulo">Actividades que tienes asignadas</span>
                </div>
            </div>

            <LoaderScreen loader={loader} />

            <div className="gestionti-table-wrap scroll-4">
                {!loader.mostrar && actividades.length === 0 ? (
                    <div className="gestionti-empty">No tienes actividades asignadas.</div>
                ) : (
                    <table className="gestionti-table" cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                <th>Actividad</th>
                                <th>Solicitud #</th>
                                <th>Estimación (hrs)</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {actividades.map((a) => (
                                <tr key={a.idactividad} className="gestionti-table-row">
                                    <td className="gestionti-td-titulo">{a.descripcion}</td>
                                    <td className="gestionti-td-folio">#{a.idsolicitud}</td>
                                    <td className="gestionti-td-center">{a.estimacion_horas}</td>
                                    <td><Badge texto={a.estado} mapa={ACTIVIDAD_ESTADO_COLORES} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

// ─── Vista Admin ──────────────────────────────────────────────────────────────

// KPI de portada: agrupan los estados individuales en 4 números de un
// vistazo. No sustituyen el detalle por grupo de abajo, son el resumen
// ejecutivo arriba de todo.
const KPIS_ADMIN = [
    { key: "pendientes", label: "Pendientes de revisión", estados: ["Nueva", "En revisión"], icon: InboxOutlinedIcon, tono: "azul" },
    { key: "accion",     label: "Requieren acción",        estados: ["Pendiente de aprobación del usuario", "Repriorizada"], icon: ErrorOutlineOutlinedIcon, tono: "ambar" },
    { key: "priorizadas",label: "En ejecución",            estados: ["Priorizada", "Asignada", "En progreso", "En pruebas"], icon: GpsFixedOutlinedIcon, tono: "verde" },
    { key: "completadas",label: "Completadas",             estados: ["Completada"], icon: TaskAltOutlinedIcon, tono: "verde" },
];

// Resumen por etapa del flujo: solo el total de cada grupo. El desglose por
// estado individual (Nueva, Rechazada, En definición, etc.) vive en la
// pantalla "Solicitudes" (todavía no construida) -- aquí solo damos el
// vistazo rápido, no un formulario de filtros.
const ETAPAS_ADMIN = [
    { grupo: "Ingreso",       estados: ["Nueva", "En revisión", "Rechazada"] },
    { grupo: "Análisis",      estados: ["Aceptada", "En definición", "Pendiente de aprobación del usuario"] },
    { grupo: "Planificación", estados: ["Priorizada", "Repriorizada", "Asignada"] },
    { grupo: "Ejecución",     estados: ["En progreso", "En pruebas", "Completada"] },
];

const VistaAdmin = ({ onVerListado, onNuevaSolicitud }) => {
    const { empleado } = useGestionTI();
    const [conteo, setConteo]     = useState({});
    const [recientes, setRecientes] = useState([]);
    const [loader, setLoader]     = useState({ mostrar: true, alerta: false });

    useEffect(() => {
        request
            .get("SolicitudesTI/Listado")
            .then((result) => {
                const items = result ?? [];
                const mapa  = {};
                items.forEach((s) => {
                    mapa[s.estado] = (mapa[s.estado] || 0) + 1;
                });
                setConteo(mapa);
                setRecientes(items.slice(0, 5));
                setLoader({ mostrar: false, alerta: false });
            })
            .catch((error) => {
                setLoader({ mostrar: false, alerta: false });
                Swal.fire("GESTIÓN TI", JSON.stringify(error), "error");
            });
    }, []);

    return (
        <div className="gestionti-vista">
            <div className="gestionti-vista-header">
                <div>
                    <span className="gestionti-vista-titulo">Hola, {primerNombre(empleado)}</span>
                    <span className="gestionti-vista-subtitulo">Resumen general del módulo</span>
                </div>
                <div className="gestionti-vista-acciones">
                    <button className="gestionti-btn-secondary" onClick={onVerListado}>
                        Ver todas
                    </button>
                    <button className="gestionti-btn-primary" onClick={onNuevaSolicitud}>
                        <AddIcon /> Nueva solicitud
                    </button>
                </div>
            </div>

            <LoaderScreen loader={loader} />

            {!loader.mostrar && (
                <div className="gestionti-vista-scroll scroll-4">
                    <div className="gestionti-kpi-grid">
                        {KPIS_ADMIN.map(({ key, label, estados, icon: Icon, tono }) => {
                            const valor = estados.reduce((acc, e) => acc + (conteo[e] ?? 0), 0);
                            return (
                                <div key={key} className={`gestionti-kpi-card gestionti-kpi-card--${tono}`}>
                                    <div className="gestionti-kpi-card-head">
                                        <span className="gestionti-kpi-label">{label}</span>
                                        <Icon className="gestionti-kpi-icon" fontSize="small" />
                                    </div>
                                    <span className="gestionti-kpi-valor">{valor}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="gestionti-etapas-row">
                        {ETAPAS_ADMIN.map(({ grupo, estados }) => {
                            const total = estados.reduce((acc, e) => acc + (conteo[e] ?? 0), 0);
                            return (
                                <div key={grupo} className="gestionti-etapa-card">
                                    <span className="gestionti-etapa-label">{grupo}</span>
                                    <span className="gestionti-etapa-valor">{total}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="gestionti-recientes">
                        <span className="gestionti-recientes-titulo">Solicitudes recientes</span>
                        {recientes.length === 0 ? (
                            <div className="gestionti-empty">Sin solicitudes registradas todavía.</div>
                        ) : (
                            <div className="gestionti-recientes-lista">
                                {recientes.map((s) => (
                                    <div
                                        key={s.idsolicitud}
                                        className="gestionti-reciente-row"
                                        onClick={() => onVerListado?.()}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <span className="gestionti-reciente-titulo">{s.titulo}</span>
                                        <Badge texto={s.estado} mapa={ESTADO_COLORES} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Componente principal (pantalla "Principal") ──────────────────────────────

export const GestionTIScreen = () => {
    const history = useHistory();
    const { rol }  = useGestionTI();

    const onNuevaSolicitud = () => history.push("/GestionTI/NuevaSolicitud");
    // La pantalla de listado todavía no existe (próxima pantalla a construir).
    const onVerListado     = () => history.push("/GestionTI/Solicitudes");

    return (
        <>
            {rol === "Usuario"   && <VistaUsuario   onNuevaSolicitud={onNuevaSolicitud} />}
            {rol === "Developer" && <VistaDeveloper />}
            {rol === "Admin"     && (
                <VistaAdmin
                    onVerListado={onVerListado}
                    onNuevaSolicitud={onNuevaSolicitud}
                />
            )}
        </>
    );
};
